import { forwardRef } from 'react';
import { cn } from '../../../lib/cn';
import { ChevronGlyph } from './icons';

/**
 * ProfileHead.
 *
 * CSS selector: `organisms.css .ds-profilehead` with `__name`, `__handle`,
 * `__since`.
 *
 * Doctrine: a modest 23px header, deliberately smaller than a screen title.
 * The profile is not the loudest thing on the page, the actions under it are.
 * Compose the avatar atom into the `avatar` slot rather than sizing an image
 * here, so avatar shape stays owned by one component.
 */
export const ProfileHead = forwardRef(function ProfileHead(
  { className, avatar, name, handle, since, children, ...rest },
  ref,
) {
  return (
    <div ref={ref} className={cn('ds-profilehead', className)} {...rest}>
      {avatar}
      <h2 className="ds-profilehead__name">{name}</h2>
      {handle && <p className="ds-profilehead__handle">{handle}</p>}
      {since && <p className="ds-profilehead__since">{since}</p>}
      {children}
    </div>
  );
});

/**
 * ProfileBar.
 *
 * CSS selector: `organisms.css .ds-profilebar` with `__meta`, `__name`, `__sub`.
 *
 * Doctrine: the tappable sibling of `ProfileHead`. This one carries a hairline
 * border and a small shadow straight from the CSS, which is the single
 * exception in this file to "borderless, tone lift depth", and it is there
 * because the bar reads as a control rather than as content. Do not copy that
 * treatment onto other rows.
 */
export const ProfileBar = forwardRef(function ProfileBar(
  { className, avatar, name, sub, ...rest },
  ref,
) {
  return (
    <button ref={ref} type="button" className={cn('ds-profilebar', className)} {...rest}>
      {avatar}
      <span className="ds-profilebar__meta">
        <span className="ds-profilebar__name">{name}</span>
        {sub && <span className="ds-profilebar__sub">{sub}</span>}
      </span>
      <ChevronGlyph direction="right" />
    </button>
  );
});

export default ProfileHead;
